const SKOPJE_TIME_ZONE = "Europe/Skopje";

const SKOPJE_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
    timeZone: SKOPJE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    hourCycle: "h23",
});

const ISO_WITH_TIMEZONE_RE = /(Z|[+-]\d{2}:\d{2})$/i;
const NAIVE_DATE_RE =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2})(?::(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?)?$/;

const parseDateParts = (date: Date) => {
    const parts = SKOPJE_PARTS_FORMATTER.formatToParts(date);
    const values = Object.fromEntries(
        parts
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)])
    );

    return {
        year: values.year,
        month: values.month,
        day: values.day,
        hour: values.hour,
        minute: values.minute,
        second: values.second,
    };
};

const getSkopjeOffsetMs = (date: Date): number => {
    const parts = parseDateParts(date);
    const skopjeAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
        date.getUTCMilliseconds()
    );

    return skopjeAsUtc - date.getTime();
};

const parseNaiveSkopjeMs = (value: string): number => {
    const match = NAIVE_DATE_RE.exec(value.trim());
    if (!match) {
        return NaN;
    }

    const [
        ,
        yearText,
        monthText,
        dayText,
        hourText = "00",
        minuteText = "00",
        secondText = "00",
        fractionText = "0",
    ] = match;

    const millisecond = Number(fractionText.padEnd(3, "0").slice(0, 3));
    const naiveUtcMs = Date.UTC(
        Number(yearText),
        Number(monthText) - 1,
        Number(dayText),
        Number(hourText),
        Number(minuteText),
        Number(secondText),
        millisecond
    );

    const initialOffset = getSkopjeOffsetMs(new Date(naiveUtcMs));
    let resolvedMs = naiveUtcMs - initialOffset;
    const correctedOffset = getSkopjeOffsetMs(new Date(resolvedMs));
    if (correctedOffset !== initialOffset) {
        resolvedMs = naiveUtcMs - correctedOffset;
    }

    return resolvedMs;
};

const parseSkopjeDate = (value: string | Date | null | undefined): Date | null => {
    if (value == null || value === "") {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const timestamp = ISO_WITH_TIMEZONE_RE.test(trimmed)
        ? Date.parse(trimmed)
        : parseNaiveSkopjeMs(trimmed);

    if (!Number.isFinite(timestamp)) {
        return null;
    }

    return new Date(timestamp);
};

export const parseSkopjeDateMs = (
    value: string | Date | null | undefined
): number => {
    const parsed = parseSkopjeDate(value);
    return parsed ? parsed.getTime() : NaN;
};
